import { FC } from 'react';
import styled from 'styled-components';

interface Props {
	text: string;
	href?: string;
	onClick?: () => void;
}

const DownloadButton: FC<Props> = ({ text, href, onClick }) =>
	href ? (
		<ButtonWrapper>
			<Button>
				<Link href={href} rel="noopener noreferrer" target="_blank">
					{text}
				</Link>

				<AnimatedGradient />
			</Button>
		</ButtonWrapper>
	) : (
		<ButtonWrapper onClick={onClick}>
			<Button>
				<LinkWrapper>{text}</LinkWrapper>

				<AnimatedGradient />
			</Button>
		</ButtonWrapper>
	);

const AnimatedGradient = styled.span`
	display: block;
	position: absolute;
	width: 100%;
	height: 100%;
	background: linear-gradient(
		180deg,
		rgba(115, 96, 242, 0) 0%,
		var(--color-purple-70) 100%
	);
	transform: translateY(100%);
	z-index: 2200;
	transition: 0.3s linear;
`;

const ImageWrapper = styled.span`
	margin-right: 28px;
	margin-left: 10px;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: var(--color-white);
	border-radius: 50%;
	transition: 0.4s;

	img {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		transition: 0.4s;
	}
`;

const ButtonWrapper = styled.button`
	position: relative;
	background: none;
	border: none;
	outline: none;
	margin: 0 auto;

	&:after {
		content: '';
		z-index: 30;
		position: absolute;
		width: 146px;
		height: 50px;
		bottom: -10px;
		left: 50%;
		transform: translateX(-50%);
		background-color: var(--color-blue);
		opacity: 0.3;
		filter: blur(32px);
		border-radius: 20px;

		@media (max-width: 600px) {
			opacity: 0;
			display: none;
		}
	}

	&:before {
		content: '';
		z-index: 30;
		position: absolute;
		width: 146px;
		height: 50px;
		bottom: -10px;
		left: 50%;
		transform: translateX(-50%);
		opacity: 0.3;
		background-color: var(--color-blue);
		opacity: 0.2;
		filter: blur(32px);
		border-radius: 20px;

		@media (max-width: 600px) {
			opacity: 0;
			display: none;
		}
	}
`;

const Link = styled.a`
	position: relative;
	z-index: 2300;
	display: flex;
	align-items: center;
	text-decoration: none;
	width: 100%;
	text-align: center;
	color: var(--color-white);
	justify-content: center;
`;

const Button = styled.span`
	width: 360px;
	padding: 22px 0;
	max-width: 100%;
	display: flex;
	overflow: hidden;
	align-items: center;
	position: relative;
	cursor: pointer;
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 14px;
	line-height: 26px;
	text-transform: uppercase;
	background: linear-gradient(
		180deg,
		var(--color-purple) 0%,
		var(--color-purple-80) 100%
	);
	--smooth-corners: 19.34, 4.6;
	mask-image: paint(smooth-corners);

	z-index: 100;

	@media (max-width: 600px) {
		width: 250px;
		padding: 17px 0;
	}

	@media (max-width: 530px) {
		font-size: 13px;
	}

	span {
		display: block;
	}

	&:hover {
		${AnimatedGradient} {
			transform: translateY(0);
		}

		${ImageWrapper} {
			transform: translateX(4px) scale(1.1);

			img {
				transform: translate(-50%, -50%) scale(0.9);
			}
		}
	}

	@media (max-width: 530px) {
		--smooth-corners: 23, 5;
		mask-image: paint(smooth-corners);
	}
`;

const LinkWrapper = styled.span`
	position: relative;
	z-index: 2300;
	display: flex;
	align-items: center;
	text-decoration: none;
	width: 100%;
	text-align: center;
	color: var(--color-white);
`;

export default DownloadButton;
